import { useState } from "react";
import { Trash2, X } from "@fg/icons";
import { cn } from "@/lib/utils";
import { PALETTE, type FieldInfo, type PaletteKind } from "./formSets";

const VALIDATIONS: Array<{ type: FieldInfo["validation"]["type"]; label: string }> = [
    { type: "none", label: "No validation" },
    { type: "number", label: "Number" },
    { type: "integer", label: "Whole number" },
    { type: "email", label: "Email address" },
    { type: "phone", label: "Phone number" },
    { type: "date", label: "Date (yyyy-mm-dd)" },
    { type: "length", label: "Text length" },
    { type: "regex", label: "Matches a pattern" }
];

export interface FormBuilderProps {
    /** The palette entry waiting for a click on the page. */
    armed: PaletteKind | null;
    onArm(kind: PaletteKind | null): void;
    field: FieldInfo | null;
    onPatch(patch: Partial<FieldInfo>): void;
    onAddOption(): void;
    onDelete(): void;
    onClose(): void;
}

const input = "h-7 w-full rounded border border-border bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring";

/** A Google-Forms-style builder: question types to drag onto the page, and the settings of the field that is selected. */
export function FormBuilder(p: FormBuilderProps) {
    const f = p.field;
    const [optText, setOptText] = useState<{ id: string; text: string } | null>(null);
    const v = f?.validation;
    const hasRange = v && (v.type === "number" || v.type === "integer" || v.type === "length");
    return (
        <aside aria-label="Form builder" data-form-builder className="absolute bottom-3 right-3 top-3 z-30 flex w-72 flex-col overflow-hidden rounded-lg border bg-card text-xs text-card-foreground shadow-xl">
            <div className="flex items-center justify-between border-b px-3 py-2">
                <b className="text-sm">Form builder</b>
                <button type="button" aria-label="Close form builder" title="Close" onClick={p.onClose} className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-3">
                <p className="mb-2 text-muted-foreground">Drag a question type onto the page, or click one and then click where it goes.</p>
                <div className="grid grid-cols-2 gap-1.5">
                    {PALETTE.map((it) => (
                        <button
                            key={it.kind}
                            type="button"
                            draggable
                            title={it.hint}
                            aria-pressed={p.armed === it.kind}
                            data-palette={it.kind}
                            onDragStart={(e) => { e.dataTransfer.setData("application/x-fg-field", it.kind); e.dataTransfer.effectAllowed = "copy"; }}
                            onClick={() => p.onArm(p.armed === it.kind ? null : it.kind)}
                            className={cn("flex h-9 cursor-grab items-center rounded border px-2 text-left hover:bg-accent active:cursor-grabbing", p.armed === it.kind && "border-primary bg-primary/10 font-medium")}
                        >
                            {it.label}
                        </button>
                    ))}
                </div>

                {f ? (
                    <section className="mt-4 space-y-2.5 border-t pt-3" aria-label="Question settings">
                        <h3 className="text-sm font-semibold">Question settings</h3>
                        <label className="block">Question<input className={input} value={f.label} onChange={(e) => p.onPatch({ label: e.target.value })} /></label>
                        <label className="block">Field name<input className={input} value={f.name} onChange={(e) => p.onPatch({ name: e.target.value.replace(/[^\w.-]/g, "_") })} /></label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-1.5"><input type="checkbox" checked={f.required} onChange={(e) => p.onPatch({ required: e.target.checked })} /> Required</label>
                            <label className="flex items-center gap-1.5"><input type="checkbox" checked={f.readOnly} onChange={(e) => p.onPatch({ readOnly: e.target.checked })} /> Read-only</label>
                        </div>
                        {f.ftype === "text" ? (
                            <>
                                <label className="flex items-center gap-1.5"><input type="checkbox" checked={f.multiline} onChange={(e) => p.onPatch({ multiline: e.target.checked })} /> Several lines</label>
                                <label className="block">Longest answer (0 = no limit)<input type="number" min={0} className={input} value={f.maxLen} onChange={(e) => p.onPatch({ maxLen: Math.max(0, Number(e.target.value) || 0) })} /></label>
                                <label className="block">Answer already filled in<input className={input} value={f.defaultValue} onChange={(e) => p.onPatch({ defaultValue: e.target.value })} /></label>
                                <fieldset className="space-y-1.5 rounded border p-2">
                                    <legend className="px-1">Response validation</legend>
                                    <select aria-label="Validation" className={input} value={v?.type ?? "none"} onChange={(e) => p.onPatch({ validation: { ...(v ?? {}), type: e.target.value as FieldInfo["validation"]["type"] } })}>
                                        {VALIDATIONS.map((o) => <option key={o.type} value={o.type}>{o.label}</option>)}
                                    </select>
                                    {hasRange ? (
                                        <div className="flex gap-1.5">
                                            <label className="flex-1">Min<input type="number" className={input} value={v?.min ?? ""} onChange={(e) => p.onPatch({ validation: { ...v!, min: e.target.value === "" ? undefined : Number(e.target.value) } })} /></label>
                                            <label className="flex-1">Max<input type="number" className={input} value={v?.max ?? ""} onChange={(e) => p.onPatch({ validation: { ...v!, max: e.target.value === "" ? undefined : Number(e.target.value) } })} /></label>
                                        </div>
                                    ) : null}
                                    {v?.type === "regex" ? <label className="block">Pattern<input className={input} placeholder="^[A-Z]{3}\d{4}$" value={v.pattern ?? ""} onChange={(e) => p.onPatch({ validation: { ...v, pattern: e.target.value } })} /></label> : null}
                                    {v && v.type !== "none" ? <label className="block">Message when wrong<input className={input} value={v.message ?? ""} onChange={(e) => p.onPatch({ validation: { ...v, message: e.target.value } })} /></label> : null}
                                </fieldset>
                            </>
                        ) : null}
                        {f.ftype === "combo" || f.ftype === "list" ? (
                            <label className="block">Options, one per line
                                <textarea rows={5} className={cn(input, "h-auto py-1")}
                                    value={optText?.id === f.id ? optText.text : f.options.join("\n")}
                                    onChange={(e) => { setOptText({ id: f.id, text: e.target.value }); p.onPatch({ options: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) }); }}
                                    onBlur={() => setOptText(null)} />
                            </label>
                        ) : null}
                        {f.ftype === "radio" || f.ftype === "check" ? (
                            <>
                                <label className="block">This choice's value<input className={input} value={f.exportValue} onChange={(e) => p.onPatch({ exportValue: e.target.value })} /></label>
                                <button type="button" onClick={p.onAddOption} className="h-7 rounded border px-2 hover:bg-accent">Add option</button>
                                <p className="text-muted-foreground">Edit an option's label by double-clicking its text on the page.</p>
                            </>
                        ) : null}
                        <button type="button" onClick={p.onDelete} className="flex h-7 items-center gap-1.5 rounded border px-2 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"><Trash2 className="h-3.5 w-3.5" /> Delete field</button>
                    </section>
                ) : <p className="mt-4 border-t pt-3 text-muted-foreground">Select a field on the page to set its name, whether it is required, its options and its validation.</p>}
            </div>
        </aside>
    );
}
